import { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import './App.css';
import AboutUs from './pages/AboutUs/AboutUs';
import './components/components.css'
import APIDocsTemplate from './pages/APIDocsTemplate/APIDocsTemplate';
import Banner from './components/Banner/Banner'
import BecomeAPIProvider from './pages/BecomeAPIProvider/BecomeAPIProvider';
import Faq from './pages/FAQ/Faq';
import Footer from "./components/Footer/Footer";
import HelpAndSupport from './pages/HelpAndSupport/HelpAndSupport';
import Homepage from './pages/Homepage/Homepage';
import SupportTicket from './pages/SupportTicket/SupportTicket';
import Credentials from './pages/Credentials/Credentials';
import APILibrary from './pages/APILibrary/APILibrary';
import Page404 from './pages/ErrorPages/Page404';
import SwaggerView from './pages/SwaggerViewTemplate/SwaggerView';
import { RESTRICTEDAPIS } from "./helpers/enums/restrictedApiUrls";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import MsalAuthHandler from "./helpers/msalAuthHandler";
import Spinner from "./helpers/spinner";
import WorkingWithCdcApis from "./pages/BecomeAPIProvider/workingWithCdcApis";
import PublishingApis from "./pages/BecomeAPIProvider/PublishingApis";

function App() {
  // Keep Router at the top level and delegate logic to AppContent which can use hooks
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

function AppContent() {
  const [loading, setLoading] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();

  useEffect(() => {
    const fragment = window.location.hash;

    if (fragment) {
      const params = new URLSearchParams(fragment.slice(1)); // Remove the # and parse
      const token = params.get("id_token");

      if (token) {
        sessionStorage.setItem("jwt", token);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
    // If MSAL has finished authenticating, and we have a saved return URL,
    // navigate to it and clear the saved redirect flags. This is more reliable
    // than relying on presence of tokens in the fragment.
    if (isAuthenticated) {
      try {
        const returnUrl = sessionStorage.getItem('msalRedirectReturnUrl');
        if (returnUrl) {
          sessionStorage.removeItem('msalRedirectReturnUrl');
          sessionStorage.removeItem('msalRedirectInProgress');
          if (window.location.pathname !== returnUrl) {
            navigate(returnUrl, { replace: true });
            return; // navigation will re-render
          }
        } else {
          // nothing to restore; ensure flags cleared
          sessionStorage.removeItem('msalRedirectInProgress');
        }
      } catch (e) {
        // ignore storage errors
      }
    }
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, isAuthenticated]);

  const checkAuth = () => {
    // Only run when MSAL is idle to avoid racing with redirect handling
    if (inProgress === InteractionStatus.None) {
      for (const api of RESTRICTEDAPIS) {
        if (window.location.pathname.includes(api) && !isAuthenticated) {
          // Avoid starting another redirect if one was already started
          const redirectInProgress = sessionStorage.getItem('msalRedirectInProgress');
          if (redirectInProgress) {
            // don't trigger another redirect while MSAL is returning to the app
            return;
          }
          // Prefer starting the MSAL redirect login flow so user can authenticate
          if (instance && typeof instance.loginRedirect === 'function') {
            try {
              // Save desired return URL so we can navigate back after auth
              sessionStorage.setItem('msalRedirectReturnUrl', window.location.pathname + window.location.search);
              sessionStorage.setItem('msalRedirectInProgress', '1');
            } catch (e) {
              // ignore storage errors
            }
            instance.loginRedirect();
          } else {
            // Fallback: navigate to homepage if MSAL instance isn't ready
            navigate('/', { replace: true });
          }
          return;
        }
      }
    }
  };

  return (
    <>
      <MsalAuthHandler setLoading={setLoading} />
      <div className="flex col sb vh-100">
        {loading && <Spinner />}
        <div>
          <Banner />
          <Routes>
            <Route path="/" element={<Homepage />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/docs/:slug/swagger/:apiname" element={<SwaggerView />} />
            <Route path="/docs/:slug/*" element={<APIDocsTemplate />} />
            <Route path="/apis" element={<APILibrary />} />
            <Route path="/help" element={<HelpAndSupport />} />
            <Route path="/help/become-provider" element={<BecomeAPIProvider />} />
            <Route path="/help/become-provider/working-with-cdc-apis" element={<WorkingWithCdcApis />} />
            <Route path="/help/become-provider/publishing-apis" element={<PublishingApis />} />
            <Route path="/support-ticket/submit-support-ticket" element={<SupportTicket />} />
            <Route path="/drs" element={<Credentials />} />
            <Route path="/help/faq" element={<Faq />} />
            <Route path="*" element={<Page404 />} />
          </Routes>
        </div>
        <div>
          <Footer />
        </div>
      </div>
    </>
  );
}

export default App;
